import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { EraseUserRequestSchema, type User } from '@reflex/shared';
import { Trash2 } from 'lucide-react';
import {
  useAdminUserErasure,
  useAppAdmins,
  useEraseAdminUserMutation,
  useReviewAdminUserErasureMutation,
} from '@reflex/ui/client/api/admin';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { PageDescription, SectionLabel } from '@reflex/ui/components/layout/Typography';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { Label } from '@reflex/ui/components/ui/label';
import { Badge } from '@reflex/ui/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@reflex/ui/components/ui/dialog';
import { formatDateTime } from '@reflex/ui/lib/format';

const reviewSchema = z.object({
  confirmUserId: z.string().min(1),
  externalCredentialsRevoked: z.boolean().refine(Boolean),
  processorsAndContentReviewed: z.boolean().refine(Boolean),
  backupRetentionApplied: z.boolean().refine(Boolean),
});

export function UserErasureSection({ user, isSelf }: { user: User; isSelf: boolean }) {
  const receiptQuery = useAdminUserErasure(user.id);
  const admins = useAppAdmins();
  const erase = useEraseAdminUserMutation();
  const review = useReviewAdminUserErasureMutation();
  const [open, setOpen] = useState(false);
  const form = useForm({
    resolver: zodResolver(EraseUserRequestSchema),
    defaultValues: { confirmUserId: '', reason: 'privacy_request' as const },
  });
  const reviewForm = useForm({
    resolver: zodResolver(reviewSchema),
    defaultValues: {
      confirmUserId: '',
      externalCredentialsRevoked: false,
      processorsAndContentReviewed: false,
      backupRetentionApplied: false,
    },
  });
  const confirmation = useWatch({ control: form.control, name: 'confirmUserId' });
  const reviewValues = useWatch({ control: reviewForm.control });
  const receipt = receiptQuery.data;
  const lockReason = isSelf
    ? 'You cannot erase your own account.'
    : user.isSuperAdmin || admins.data?.admins.some(({ user: admin }) => admin.id === user.id)
      ? 'Revoke platform-admin privileges before erasing this account. Super admins cannot be erased.'
      : user.status !== 'disabled' && user.status !== 'pending'
        ? 'Disable this account above before erasing it.'
        : null;
  const error = erase.error ?? review.error;
  const errorMessage =
    error instanceof Error ? error.message : 'The operation failed. Retry or contact support.';

  return (
    <SectionCard
      data-testid="user-erasure-section"
      title="Account erasure"
      icon={<Trash2 />}
      variant="destructive"
      contentClassName="space-y-4"
      description="Permanently remove this account’s profile, credentials, and access across all organizations. This cannot be undone."
    >
      <PageDescription>
        This is local account erasure, not complete anonymization or a compliance certification.
        Shared content, external systems, and backups require a separate review.
      </PageDescription>
      {receiptQuery.isPending ? (
        <PageDescription>Loading erasure status…</PageDescription>
      ) : receiptQuery.isError ? (
        <div role="alert" className="space-y-2">
          <PageDescription>
            Could not load the erasure receipt. Actions are locked until status can be verified.
          </PageDescription>
          <Button variant="outline" onClick={() => void receiptQuery.refetch()}>
            Retry erasure status
          </Button>
        </div>
      ) : receipt ? (
        <div className="space-y-4" data-testid="erasure-receipt">
          <Badge variant={receipt.followUp === 'required' ? 'destructive' : 'secondary'}>
            {receipt.followUp === 'required'
              ? 'Local erasure complete · Follow-up required'
              : 'Local erasure complete · Review recorded'}
          </Badge>
          <dl className="grid gap-3 sm:grid-cols-2">
            <div>
              <SectionLabel as="dt">Retained user ID</SectionLabel>
              <dd className="break-all font-mono text-xs">{receipt.userId}</dd>
            </div>
            <div>
              <SectionLabel as="dt">Erased at</SectionLabel>
              <dd>{formatDateTime(receipt.erasedAt)}</dd>
            </div>
            {receipt.reviewCompletedAt !== null && (
              <div>
                <SectionLabel as="dt">Review recorded at</SectionLabel>
                <dd>{formatDateTime(receipt.reviewCompletedAt)}</dd>
              </div>
            )}
          </dl>
          <PageDescription>
            The retained tombstone and historical references cannot be re-enabled. Review shared
            content, processor records, artifacts, snapshots, and backup retention separately.
          </PageDescription>
          <div className="space-y-2">
            <SectionLabel>Runloop secret mirrors</SectionLabel>
            <PageDescription>
              Remove each listed mirror from its organization’s Runloop tenant. Local erasure does
              not remove external copies.
            </PageDescription>
            {receipt.secretMirrors.length === 0 ? (
              <PageDescription>
                No secret mirrors were recorded. Provider grants and copied credentials still need
                separate review.
              </PageDescription>
            ) : (
              <ul className="space-y-2">
                {receipt.secretMirrors.map((mirror) => (
                  <li
                    key={`${mirror.organizationId}:${mirror.secretId}`}
                    className="rounded border p-3"
                  >
                    <SectionLabel>Organization</SectionLabel>
                    <code className="block break-all text-xs">{mirror.organizationId}</code>
                    <SectionLabel>Mirror name</SectionLabel>
                    <code className="block break-all text-xs">{`reflex_user_${mirror.secretId}`}</code>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {receipt.followUp === 'required' && (
            <form
              className="space-y-4"
              onSubmit={reviewForm.handleSubmit((values) => {
                if (values.confirmUserId !== user.id) return;
                review.mutate({
                  userId: user.id,
                  data: {
                    confirmUserId: values.confirmUserId,
                    externalCredentialsRevoked: true,
                    processorsAndContentReviewed: true,
                    backupRetentionApplied: true,
                  },
                });
              })}
            >
              <SectionLabel>Record external follow-up</SectionLabel>
              <PageDescription>
                Check each item only after completing the work. This records your attestation; it
                does not perform cleanup.
              </PageDescription>
              <fieldset disabled={review.isPending} className="space-y-3">
                <Label className="flex items-start gap-2">
                  <input type="checkbox" {...reviewForm.register('externalCredentialsRevoked')} />
                  External grants, credentials, and secret mirrors have been revoked or removed.
                </Label>
                <Label className="flex items-start gap-2">
                  <input type="checkbox" {...reviewForm.register('processorsAndContentReviewed')} />
                  Shared content and processor records have been reviewed.
                </Label>
                <Label className="flex items-start gap-2">
                  <input type="checkbox" {...reviewForm.register('backupRetentionApplied')} />
                  Backup retention and restore safeguards have been applied.
                </Label>
                <Label htmlFor="review-user-id">Type {user.id} to confirm review</Label>
                <Input id="review-user-id" {...reviewForm.register('confirmUserId')} />
              </fieldset>
              <Button
                type="submit"
                disabled={
                  review.isPending ||
                  reviewValues.confirmUserId !== user.id ||
                  !reviewValues.externalCredentialsRevoked ||
                  !reviewValues.processorsAndContentReviewed ||
                  !reviewValues.backupRetentionApplied
                }
              >
                {review.isPending ? 'Recording review…' : 'Record completed review'}
              </Button>
            </form>
          )}
        </div>
      ) : (
        <>
          <PageDescription>
            Before erasing: verify the request, revoke upstream credentials, stop agents and
            sandboxes, disable owned automations, stop live runs, disconnect workstations, and
            transfer management roles and crews. Only human accounts can be erased. The server
            checks prerequisites before any local cleanup.
          </PageDescription>
          {user.status === 'pending' && !lockReason && (
            <PageDescription>
              This unclaimed account can be erased without activating it. Transfer any management
              roles first.
            </PageDescription>
          )}
          {lockReason && <PageDescription>{lockReason}</PageDescription>}
          {admins.isError && (
            <div role="alert">
              <PageDescription>Could not verify platform-admin privileges.</PageDescription>
              <Button variant="outline" onClick={() => void admins.refetch()}>
                Retry privileges
              </Button>
            </div>
          )}
          <Button
            variant="destructive"
            disabled={!!lockReason || !admins.isSuccess}
            onClick={() => {
              form.reset();
              erase.reset();
              setOpen(true);
            }}
          >
            Erase account…
          </Button>
          <Dialog
            open={open}
            onOpenChange={(next) => {
              if (!erase.isPending) setOpen(next);
            }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Irreversibly erase this account?</DialogTitle>
                <DialogDescription>
                  Profile identifiers, private credentials, sessions, memberships, and personal
                  state will be removed across all organizations. Historical references remain.
                  External follow-up is required.
                </DialogDescription>
              </DialogHeader>
              <form
                className="space-y-4"
                onSubmit={form.handleSubmit((data) => {
                  if (data.confirmUserId !== user.id) return;
                  erase.mutate({ userId: user.id, data }, { onSuccess: () => setOpen(false) });
                })}
              >
                <fieldset disabled={erase.isPending} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="erasure-reason">Reason</Label>
                    <select
                      id="erasure-reason"
                      className="w-full rounded-md border bg-background p-2 text-sm"
                      {...form.register('reason')}
                    >
                      <option value="privacy_request">Privacy request</option>
                      <option value="retention_policy">Retention policy</option>
                      <option value="test_account">Test account</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="erase-user-id">Type {user.id} to confirm erasure</Label>
                    <Input id="erase-user-id" {...form.register('confirmUserId')} />
                  </div>
                </fieldset>
                {erase.isError && (
                  <div role="alert">
                    <PageDescription>
                      {errorMessage} Resolve the prerequisite or retry when the service is
                      available.
                    </PageDescription>
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={erase.isPending}
                    onClick={() => setOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="destructive"
                    disabled={erase.isPending || confirmation !== user.id}
                  >
                    {erase.isPending ? 'Erasing account…' : 'Permanently erase account'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </>
      )}
      {(erase.isError || review.isError) && !open && (
        <div role="alert">
          <PageDescription>{errorMessage}</PageDescription>
        </div>
      )}
    </SectionCard>
  );
}
